import type * as Core from '../../types/calculation-core.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
export const DEFAULT_PERCENTAGE_CONFIG = {
    metadata: {
        calculatorId: 'percentage',
        version: '1.0.0',
        effectiveFrom: '2026-08-18',
        lastVerifiedAt: '2026-08-18',
        sources: [
            {
                id: 'openstax-percent-applications',
                title: 'Prealgebra 2e — Solve General Applications of Percent',
                publisher: 'OpenStax',
                url: 'https://openstax.org/books/prealgebra-2e/pages/6-2-solve-general-applications-of-percent',
                verifiedAt: '2026-08-18',
            },
        ],
        notes: ['Saf aritmetik araçtır; mevzuata veya dönemsel tarifeye bağlı değildir.'],
    },
    maxAbsoluteInput: Number.MAX_SAFE_INTEGER,
};
const MODES: readonly string[] = [
    'percentage-of',
    'what-percentage',
    'increase-decrease',
    'percentage-change',
];
function isPercentageMode(value: unknown): value is Core.PercentageInput["mode"] {
    return typeof value === 'string' && MODES.includes(value);
}
function validateConfig(config: Core.BaseConfig): Core.InputError[] {
    const errors = [...validateMetadata(config.metadata)];
    if (!isFiniteNumber(config.maxAbsoluteInput) || config.maxAbsoluteInput <= 0) {
        errors.push({
            field: 'config.maxAbsoluteInput',
            code: 'INVALID_CONFIG',
            message: 'Azami mutlak giriş değeri sıfırdan büyük ve sonlu olmalıdır.',
        });
    }
    return errors;
}
function validateInput(input: Core.PercentageInput, config: Core.BaseConfig, mode: unknown): Core.InputError[] {
    const errors = [];
    if (!input || typeof input !== 'object') {
        return [{ field: 'input', code: 'REQUIRED', message: 'Hesaplama girdisi gereklidir.' }];
    }
    if (!isPercentageMode(mode)) {
        errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Yüzde hesaplama modu desteklenmiyor.' });
    }
    for (const field of ['x', 'y'] as const) {
        const value = input[field];
        if (!isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field.toUpperCase()} sonlu bir sayı olmalıdır.` });
        }
        else if (Math.abs(value) > config.maxAbsoluteInput) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field.toUpperCase()} izin verilen aralığın dışındadır.` });
        }
    }
    if (isPercentageMode(mode)) {
        if (mode === 'what-percentage' && input.y === 0) {
            errors.push({ field: 'y', code: 'DIVISION_BY_ZERO', message: 'Bütün (Y) sıfır olamaz.' });
        }
        if (mode === 'percentage-change' && input.x === 0) {
            errors.push({ field: 'x', code: 'DIVISION_BY_ZERO', message: 'Eski değer (X) sıfır olamaz.' });
        }
    }
    return errors;
}
function overflow(metadata: Core.CoreMetadata) {
    return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Sonuç sonlu sayı aralığını aştı.' }], metadata);
}
export function calculatePercentage(input: Core.PercentageInput, config: Core.BaseConfig = DEFAULT_PERCENTAGE_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_PERCENTAGE_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Yüzde konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    const mode = input && typeof input === 'object' ? input.mode : undefined;
    const inputErrors = validateInput(input, config, mode);
    if (inputErrors.length > 0 || !isPercentageMode(mode))
        return failure(inputErrors, metadata);
    switch (mode) {
        case 'percentage-of': {
            const result = (input.x * input.y) / 100;
            return Number.isFinite(result) ? success({ mode: mode, result }, metadata) : overflow(metadata);
        }
        case 'what-percentage': {
            const percentage = (input.x / input.y) * 100;
            return Number.isFinite(percentage)
                ? success({ mode: mode, percentage }, metadata)
                : overflow(metadata);
        }
        case 'increase-decrease': {
            const increased = input.x * (1 + input.y / 100);
            const decreased = input.x * (1 - input.y / 100);
            return Number.isFinite(increased) && Number.isFinite(decreased)
                ? success({ mode: mode, increased, decreased }, metadata)
                : overflow(metadata);
        }
        case 'percentage-change': {
            const percentageChange = ((input.y - input.x) / input.x) * 100;
            const absoluteDifference = input.y - input.x;
            return Number.isFinite(percentageChange) && Number.isFinite(absoluteDifference)
                ? success({ mode: mode, percentageChange, absoluteDifference }, metadata)
                : overflow(metadata);
        }
    }
}
